import cn from 'classnames';
import React from 'react';
import type { Poll } from 'schema/poll';
import { WebviewCloseButton } from '../../../../components/WebviewCloseButton';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';
import { inIframe, shouldHideHeader } from '../../../../utils/location';
import { Title } from '../../Title';
import { BackButton } from './BackButton';
import styles from './Header.module.css';
import { ProgressBar } from './ProgressBar';

interface CommonHeaderProps {
  title: string | null;
  isWebview: boolean;
  hasWelcome: boolean;
  progress: {
    currentQuestionIndex: number;
    previousQuestionIndex: number;
    questionCount: number;
    type: NonNullable<Poll['progressBar']>;
  };
  className?: string;
  alignSurvey: 'left' | 'center';
  onClose: VoidFunction;
}

type HeaderProps = CommonHeaderProps | (CommonHeaderProps & { showBackButton: boolean; onBack: VoidFunction });

const inlineProgress = ['numbers', 'percent'] as const;

export const Header: React.FC<HeaderProps> = ({
  className,
  isWebview,
  title,
  progress,
  hasWelcome,
  alignSurvey,
  ...props
}) => {
  const isPhone = useBreakpoint('phone');

  const isFirstShownScreen = progress.previousQuestionIndex === -1 && !hasWelcome;
  const showTitle = !!title && !inIframe() && (!isPhone || isFirstShownScreen);
  const showBackButton = 'showBackButton' in props && props.showBackButton;
  const inline = inlineProgress.some(type => type === progress.type);

  const showProgressBar = progress.type !== 'invisible';

  const showHeader = (showProgressBar || showBackButton || showTitle || isWebview) && !shouldHideHeader();

  if (!showHeader) {
    return null;
  }

  return (
    <header className={className} data-qa-type="poll.header">
      {(showBackButton || isWebview || showProgressBar) && (
        <div
          className={cn({
            [c(styles.inline)]: inline,
            [c(styles.webviewNavigation)]: isWebview,
            [c(styles.statusBarWithTitle)]: showTitle,
          })}
        >
          {showBackButton && (
            <BackButton className={cn(styles.backButton, { [c(styles.separator)]: inline })} onClick={props.onBack} />
          )}

          {isWebview && <WebviewCloseButton className={styles.webviewButton} onClick={props.onClose} />}

          {showProgressBar && progress.type !== 'invisible' && (
            <div className={cn(styles.progressBar, { [c(styles.withSpacing)]: showBackButton || isWebview })}>
              <ProgressBar {...progress} type={progress.type} />
            </div>
          )}
        </div>
      )}

      {showTitle && <Title alignSurvey={alignSurvey}>{title}</Title>}
    </header>
  );
};
